import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BwjInput } from '../../framework/entities/bwj-input';
import { IBwjInputProps } from '../../framework/components/bwj-input-base';
import { BwjValidityState } from '../../framework/entities/bwj-validity-state';
import { isNullOrUndefined, isNullOrWhitespace } from '../../framework/utils';
import { IBwjFormOption } from '../../framework/entities/bwj-form-option';
import { firstOrDefault } from '../../framework/utils/collection';
import { usePRIVATE_STYLES } from './styles';
import { BwjWarningLabel } from '../../framework/components/bwj-warning-label';
import { Text } from '@fluentui/react'
import { BwjSingleSelectField } from '../../framework/components/bwj-select-field/single';

type DurationTime = number | null | undefined;

interface IDurationFieldProps extends IBwjInputProps<DurationTime> {
    minDuration?: DurationTime;
    maxDuration?: DurationTime;
}

const BLUR_LATENCY = 100;
const MAX_DURATION = 86399;

const normalizeMinMaxDuration = (min: DurationTime, max: DurationTime): [number, number] => {
  let n = isNullOrUndefined(min) ? 0 : min!;
  let x = isNullOrUndefined(max) ? MAX_DURATION : max!;
  n = n > MAX_DURATION ? MAX_DURATION : n;
  x = x > MAX_DURATION ? MAX_DURATION : x;
  if(n > x) {
    const temp = n;
    n = x;
    x = temp;
  }

  return [n, x];
}

const generateOptions = (from: number, to: number): IBwjFormOption<number>[] => {
  const options: IBwjFormOption<number>[] = [];
  for(let i = from; i <= to; i++) {
    options.push({
      value: i,
      description: i.toString().padStart(2, '0'),
    });
  }

  return options;
}

const parseDuration = (duration: DurationTime): [number, number, number] => {
  if(isNullOrUndefined(duration)) { return [0, 0, 0]; }

  const d = duration!;
  const hour = Math.floor(d / 3600);
  const minute = Math.floor((d - (hour * 3600)) / 60);
  const second = d - ((hour * 3600) + (minute * 60));

  return [hour, minute, second];
}

const toDurationValue = (hours: number, minutes: number, seconds: number) => {
  const duration = (hours * 3600) + (minutes * 60) + seconds;
  return duration;
}

const optionToInput = (option: IBwjFormOption<number>) => new BwjInput<IBwjFormOption<number>>(option);

export const DurationField: React.FunctionComponent<IDurationFieldProps> = (props) => {
  const { input, onUpdate } = props;
  const [hourInput, setHourInput] = useState(new BwjInput<IBwjFormOption<number> | null>(null));
  const [minInput, setMinInput] = useState(new BwjInput<IBwjFormOption<number> | null>(null));
  const [secInput, setSecInput] = useState(new BwjInput<IBwjFormOption<number> | null>(null));
  const [hourOptions, setHourOptions] = useState<IBwjFormOption<number>[]>([]);
  const [minuteOptions, setMinuteOptions] = useState<IBwjFormOption<number>[]>([]);
  const [secondOptions, setSecondOptions] = useState<IBwjFormOption<number>[]>([]);
  const focusTimeout = useRef<number | undefined>();
  const gotFocus = useRef(false);

  const [minDuration, maxDuration] = normalizeMinMaxDuration(props.minDuration, props.maxDuration);
  const [minHour, minMinute, minSecond] = parseDuration(minDuration);
  const [maxHour, maxMinute, maxSecond] = parseDuration(maxDuration);

  const setInputsAndOptions = useCallback((hour: number, minute: number, second: number,
    setHour: boolean, setMinute: boolean, setSecond: boolean) => {
    const minRange = [0, 59];
    const secRange = [0, 59];

    if(hour === minHour) { minRange[0] = minMinute; }
    else if(hour === maxHour) { minRange[1] = maxMinute; }

    if(hour === minHour && minute === minMinute) { secRange[0] = minSecond; }
    else if (hour === maxHour && minute === maxMinute) { secRange[1] = maxSecond; }

    const hourOptions = generateOptions(minHour, maxHour);
    setHour && setMinute && setSecond && setHourOptions(hourOptions);

    const minuteOptions = generateOptions(minRange[0], minRange[1]);
    setHour && setMinuteOptions(minuteOptions);

    const secondOptions = generateOptions(secRange[0], secRange[1]);
    (setHour || setMinute) && setSecondOptions(secondOptions);

    if(setHour) {
      const opt = firstOrDefault(hourOptions, o => o.value === hour)!
      setHourInput(optionToInput(opt));
    }

    let minOpt = firstOrDefault(minuteOptions, o => o.value === minute);
    if(setMinute || !minOpt) {
      minOpt = minOpt ?? (minute < minuteOptions[0].value ? minuteOptions[0] : minuteOptions[minuteOptions.length - 1]);
      setMinInput(optionToInput(minOpt));
    }

    let secOpt = firstOrDefault(secondOptions, o => o.value === second);
    if(setSecond || !secOpt) {
      secOpt = secOpt ?? (second < secondOptions[0].value ? secondOptions[0] : secondOptions[secondOptions.length - 1]);
      setSecInput(optionToInput(secOpt));
    }
  }, [minHour, minMinute, minSecond, maxHour, maxMinute, maxSecond]);

  useEffect(() => {
    let duration = input.value ?? 0;
    duration = duration < minDuration ? minDuration : duration;
    duration = duration > maxDuration ? maxDuration : duration;
    // trigger a value update if the above mutates the original value.
    // To avoid nesting calls to render, we'll send the update after a short delay to
    // allow this render cycle to complete
    if(duration !== input.value) {
      window.setTimeout(() => !!onUpdate && onUpdate(duration), 10);
      return;
    }

    const [hour, minute, second] = parseDuration(duration);
    setInputsAndOptions(hour, minute, second, true, true, true);
  }, [input, minDuration, maxDuration, setInputsAndOptions, onUpdate]);

  const styles = usePRIVATE_STYLES();

  const onFocus = () => {
    if(focusTimeout.current !== undefined) { 
      window.clearTimeout(focusTimeout.current);
      focusTimeout.current = undefined;
    }

    if(gotFocus.current === false) {
      gotFocus.current = true;

      !!props.onFocus && props.onFocus();
    }
  }

  const onBlur = () => {
    !!props.onBlur && props.onBlur();
  }

  const onChildrenBlur = () => {
    if(focusTimeout.current !== undefined) { 
      window.clearTimeout(focusTimeout.current);
      focusTimeout.current = undefined;
    }

    focusTimeout.current = window.setTimeout(onBlur, BLUR_LATENCY);
    !!props.onUpdate && props.onUpdate(toDurationValue(hourInput.value!.value, minInput.value!.value, secInput.value!.value ));
  }

  const onHourChange = (hour: IBwjFormOption<number> | null) => {
    setInputsAndOptions(hour!.value, minInput.value!.value, secInput.value!.value, true, false, false);
  }

  const onMinuteChange = (minute: IBwjFormOption<number> | null) => {
    setInputsAndOptions(hourInput.value!.value, minute!.value, secInput.value!.value, false, true, false);
  }

  const onSecondChange = (second: IBwjFormOption<number> | null) => {
    setInputsAndOptions(hourInput.value!.value, minInput.value!.value, second!.value, false, false, true);
  }

  const renderInputMessage = () => {
    if(isNullOrWhitespace(input.message)) { return null; }
    const msg = input.message!;

    switch(input.validity) {
      case BwjValidityState.Warning:
        return <BwjWarningLabel text={msg} />;
      case BwjValidityState.Invalid:
        return <Text variant='small' className={styles.errorMessage}>{msg}</Text>;
      default:
        return <Text variant='small' className={styles.message}>{msg}</Text>
    }
  }

  return (
    <div id={`${props.id}-root`} className={styles.root}>
      <label htmlFor={props.id}>
        <Text variant='medium' className={props.required ? styles.requiredLabel : styles.label}>{props.label}</Text>
      </label>
      <div id={props.id} className={styles.inputContainer}>
        <BwjSingleSelectField
          id={`${props.id}-hh`}
          required={false}
          onUpdate={onHourChange}
          onFocus={onFocus}
          onBlur={onChildrenBlur}
          input={hourInput}
          options={hourOptions}
          optionDisplayKey={'description'}
          optionValueKey={'value'}
          suppressValueUpdateOnOptionsChange={true}
        />
        <span>:</span>
        <BwjSingleSelectField
          id={`${props.id}-mm`}
          required={false}
          onUpdate={onMinuteChange}
          onFocus={onFocus}
          onBlur={onChildrenBlur}
          input={minInput}
          options={minuteOptions}
          optionDisplayKey={'description'}
          optionValueKey={'value'}
          suppressValueUpdateOnOptionsChange={true}
        />
        <span>:</span>
        <BwjSingleSelectField
          id={`${props.id}-ss`}
          required={false}
          onUpdate={onSecondChange}
          onFocus={onFocus}
          onBlur={onChildrenBlur}
          input={secInput}
          options={secondOptions}
          optionDisplayKey={'description'}
          optionValueKey={'value'}
          suppressValueUpdateOnOptionsChange={true}
        />
      </div>
      {!!input.message &&
      <div>
        {renderInputMessage()}
      </div>}
    </div>
  );
};